import type { CourseAccess, CourseSummary, MyCreatorSubscription, PublicCourse } from '@mlm/shared';
import type { StorePaymentMethod } from './api';

/**
 * What the course page offers this member, decided in one place so the page,
 * the phone app and the tests agree.
 *
 * Inside the PHONE APP nothing is sold: a course or subscription is digital
 * content, and the store rules for apps put that behind the store's own
 * billing. So, exactly like Success Pro on the upgrade page, the app does not
 * sell it, and by default does not link out to buy it either; what the member
 * already has opens here on its own. A tenant may switch on a link to the same
 * course on the website (`lmsAppWebLink`), accepting the Play policy risk.
 * Free courses enroll anywhere: nothing is paid.
 */
export type CourseAction =
  /** Enrolled with live access: straight to the player. */
  | { kind: 'continue' }
  /** Free, or covered by access they already hold (a subscription): enroll, no order. */
  | { kind: 'enroll' }
  /** A paid course, bought once. */
  | { kind: 'buy' }
  /** A subscribers-only course under the per-creator model: pick a period. */
  | { kind: 'subscribe'; plans: NonNullable<PublicCourse['subscription']>['plans'] }
  /** In the phone app, where nothing digital is sold. */
  | { kind: 'outside_app' }
  /**
   * In the phone app with the tenant's website link on (`lmsAppWebLink`): the
   * same course, opened on the website where it is sold.
   */
  | { kind: 'on_web' }
  /** Nothing on sale that opens it (no plan switched on, or the tenant-wide plan). */
  | { kind: 'unavailable' };

export function courseAction(args: {
  course: Pick<PublicCourse, 'accessLevel' | 'subscription'>;
  /** From the player read; null while it loads. */
  access: CourseAccess | null;
  native: boolean;
  /** `lmsAppWebLink`: the phone app may hand the member to the website. */
  webLink?: boolean;
}): CourseAction {
  const { course, access } = args;
  if (access?.allowed && access.enrolled) return { kind: 'continue' };
  if (access?.allowed || course.accessLevel === 'free') return { kind: 'enroll' };
  const offer = webOffer(course);
  // Nothing is sold anywhere: say so in the app too, rather than pointing at a
  // website that has nothing to offer either.
  if (offer.kind === 'unavailable') return offer;
  if (args.native) return args.webLink ? { kind: 'on_web' } : { kind: 'outside_app' };
  return offer;
}

/** What the website sells for a course the member cannot open yet. */
function webOffer(course: Pick<PublicCourse, 'accessLevel' | 'subscription'>): CourseAction {
  if (course.accessLevel === 'subscriber') {
    const sub = course.subscription;
    if (!sub || sub.model !== 'creator' || sub.plans.length === 0) return { kind: 'unavailable' };
    return { kind: 'subscribe', plans: sub.plans };
  }
  return { kind: 'buy' };
}

/**
 * What a course page offers someone who is NOT signed in, when the tenant shows
 * its courses to visitors (`lmsPublicCatalog`).
 *
 * Joining is creating an account that carries the course
 * (`/signup?join=<productId>`), which comes back to this page to pay. In the
 * PHONE APP a paid course is not joined there: the app sells nothing digital,
 * so it opens the same course on the website (`lmsAppWebLink`, a Play policy
 * risk the tenant opts into) or says joining happens outside the app and links
 * nowhere. A free course is joined anywhere, since nothing is paid.
 */
export type VisitorAction =
  /** Create an account here, carrying the course. */
  | { kind: 'join' }
  /** Phone app: open the same course on the website, to join and pay there. */
  | { kind: 'join_on_web' }
  /** Phone app with the website link off: joining happens outside the app. */
  | { kind: 'outside_app' }
  /** Invitation-only, and a course does not count as the invitation here. */
  | { kind: 'invite_only' };

export function visitorAction(args: {
  course: Pick<PublicCourse, 'accessLevel'>;
  native: boolean;
  /** `lmsAppWebLink`. */
  webLink: boolean;
  signupMode: string;
  /** `campaignPageSignupAllowed`: under links-only, a course counts as the invitation. */
  joinAllowed: boolean;
}): VisitorAction {
  const canJoin = args.signupMode !== 'links_only' || args.joinAllowed;
  if (!args.native || args.course.accessLevel === 'free') {
    return canJoin ? { kind: 'join' } : { kind: 'invite_only' };
  }
  if (!args.webLink) return { kind: 'outside_app' };
  return canJoin ? { kind: 'join_on_web' } : { kind: 'invite_only' };
}

/** The same course on the website (the phone app's link out). */
export function courseWebUrl(base: string, productId: string): string {
  return `${base.replace(/\/+$/, '')}/learn/c/${encodeURIComponent(productId)}`;
}

/**
 * A `?next=` value is trusted only as a path inside this app: one leading
 * slash, never two and never a backslash (`//host` and `/\host` leave the
 * site in a browser). Anything else is dropped.
 */
export function safeNextPath(raw: string | null | undefined): string | null {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return null;
  return raw;
}

/**
 * The creators whose subscription the member holds right now (`active`: the
 * furthest unrevoked window has not ended). A subscribers-only course by one of
 * them is already open to this member, so it reads "Included" and never as
 * something to buy. Wording only: the course page still asks the server.
 */
export function subscribedCreators(
  subs: ReadonlyArray<Pick<MyCreatorSubscription, 'creatorUserId' | 'active'>> | undefined,
): Set<string> {
  return new Set((subs ?? []).filter((s) => s.active).map((s) => s.creatorUserId));
}

/** A subscribers-only course that one of the member's live subscriptions opens. */
export function includedInSubscription(
  course: Pick<CourseSummary, 'accessLevel' | 'creatorUserId'>,
  creators: ReadonlySet<string>,
): boolean {
  return (
    course.accessLevel === 'subscriber' &&
    course.creatorUserId !== null &&
    creators.has(course.creatorUserId)
  );
}

/**
 * The methods a course or plan may be paid with. Digital, so only methods
 * switched on for virtual orders; cash only (a course has no GC or coin price);
 * a wallet with no slot behind it can only fail. A THIRD-PARTY creator's order
 * takes no online gateway at all, only a manual method or the cash wallet.
 */
export function coursePaymentMethods(
  methods: StorePaymentMethod[],
  seller: { isHouse: boolean },
): StorePaymentMethod[] {
  return methods.filter((m) => {
    if (!m.virtualEnabled || m.currency !== 'cash') return false;
    if (m.settlement === 'wallet' && m.balance === null) return false;
    if (m.settlement === 'cod_manual') return false;
    if (!seller.isHouse && m.settlement === 'gateway') return false;
    return true;
  });
}
